import axios from 'axios';
import { BACKEND_MAIN_URL } from '../config';
import { projectAuthHeaders } from '../projectAuth';
import { cacheProject, getProject } from './projectRepository';
import { acknowledgeProjectFile, listQueuedProjectFiles, markQueuedFileActive, markQueuedFileStatus } from './projectFileQueue';

const uploads = new Map();
const lockKey = (account, project) => JSON.stringify([account, project]);

async function syncProject(account, project, controller) {
  let count = 0;
  const files = await listQueuedProjectFiles(account, project);
  for (const file of files) {
    if (controller.signal.aborted || navigator.onLine === false || localStorage.getItem('userId') !== account) break;
    if (file.status === 'failed') continue;
    if (!(await markQueuedFileActive(account, project, file.id, true))) continue;
    try {
      if (controller.signal.aborted || navigator.onLine === false || localStorage.getItem('userId') !== account) break;
      const form = new FormData();
      form.append('file', file.blob, file.name);
      form.append('category', file.category);
      form.append('client_upload_id', file.id);
      const { data } = await axios.post(`${BACKEND_MAIN_URL}/api/projects/${encodeURIComponent(project)}/files`, form,
        { headers: projectAuthHeaders(), signal: controller.signal });
      if (controller.signal.aborted || localStorage.getItem('userId') !== account) break;
      await acknowledgeProjectFile(account, project, file.id, { ...data, localFile: file.blob });
      count += 1;
      try {
        const cached = await getProject(account, project);
        if (cached && !cached.files?.some((item) => item.id === data.id)) {
          await cacheProject(account, { ...cached, files: [...(cached.files || []), data], file_count: (cached.files || []).length + 1 });
        }
      } catch { /* The server acknowledged the file; do not retry it to repair metadata. */ }
    } catch (error) {
      const status = error?.response?.status;
      // Validation/permission failures need an explicit retry after the user
      // changes the project; network and server failures stay queued.
      await markQueuedFileStatus(account, project, file.id,
        status >= 400 && status < 500 && status !== 401 ? 'failed' : 'pending');
    } finally {
      await markQueuedFileActive(account, project, file.id, false);
    }
  }
  return count;
}

export async function syncPendingProjectFiles(userId, { projectId } = {}) {
  if (!userId || navigator.onLine === false) return 0;
  const account = String(userId);
  const files = await listQueuedProjectFiles(account, projectId);
  const projects = [...new Set(files.map((file) => file.projectId))];
  const results = await Promise.all(projects.map((project) => {
    const key = lockKey(account, project);
    if (uploads.has(key)) return uploads.get(key).promise;
    const controller = new AbortController();
    const promise = syncProject(account, project, controller).finally(() => uploads.delete(key));
    uploads.set(key, { promise, controller, account });
    return promise;
  }));
  return results.reduce((sum, count) => sum + count, 0);
}

export async function cancelProjectFileUploads(userId) {
  const account = String(userId);
  const current = [...uploads.values()].filter((item) => item.account === account);
  current.forEach((item) => item.controller.abort());
  await Promise.allSettled(current.map((item) => item.promise));
}
